import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { BRANDS, CONTACT, LEMONADE_SECTIONS } from "@/data/products";
import { useProducts } from "@/context/ProductsContext";
import BulkOrderForm from "@/components/BulkOrderForm";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const DarkHero = ({ brand, isBling }) => (
  <section
    className={`${
      isBling
        ? "bg-gradient-to-b from-purple-700/80 via-purple-950/50 to-[#0a0a0a]"
        : "bg-gradient-to-b from-amber-800/40 via-[#171006] to-[#0a0a0a]"
    }`}
  >
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-16 sm:pt-14 sm:pb-20">
      <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to hub
      </Link>
      <div className="mt-10 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div {...fade(0.1)}>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300/90">{brand.eyebrow}</p>
          <h1 className="mt-4 font-serif2 font-semibold text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]" data-testid={`shop-${brand.slug}-title`}>
            {brand.name}
          </h1>
          <p className="mt-3 text-lg text-zinc-300">{brand.tagline}</p>
          <div className="mt-6">
            <a
              href="#collection"
              data-testid="scroll-products"
              className={`inline-block rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:scale-105 ${
                isBling ? "bg-purple-500 text-white" : "bg-amber-400 text-black"
              }`}
            >
              {brand.cta}
            </a>
          </div>
          <div className="mt-6 rounded-xl border border-white/15 bg-white/5 backdrop-blur px-5 py-3.5 max-w-md">
            <p className="text-sm text-zinc-300">{brand.note}</p>
          </div>
        </motion.div>
        <motion.div {...fade(0.25)} className="hidden lg:block">
          <img src={brand.flyer} alt={`${brand.name} flyer`} className="rounded-2xl w-full max-w-lg ml-auto shadow-2xl" />
        </motion.div>
      </div>
    </div>
  </section>
);

const LemonadeHero = ({ brand }) => (
  <section className="bg-gradient-to-b from-pink-200 via-rose-100 to-amber-50 text-[#1a1a1a]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-16 sm:pt-14 sm:pb-20">
      <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#1a1a1a] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to hub
      </Link>
      <div className="mt-10 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div {...fade(0.1)}>
          <p className="text-xs uppercase tracking-[0.3em] text-pink-500 font-semibold">{brand.eyebrow}</p>
          <h1 className="mt-4 font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl text-[#1a1a1a]" data-testid="shop-lemonade-title">
            {brand.name}
          </h1>
          <p className="mt-3 text-lg text-zinc-600">{brand.tagline}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="#collection"
              data-testid="scroll-products"
              className="inline-block rounded-full bg-pink-500 text-white px-6 py-3 text-sm font-semibold transition-transform hover:scale-105"
            >
              {brand.cta}
            </a>
            <span className="inline-flex items-center rounded-full border-2 border-[#1a1a1a] bg-white shadow-[4px_4px_0_#ec4899] px-5 py-2.5 text-sm font-semibold">
              📍 {CONTACT.location}
            </span>
          </div>
          <div className="mt-6 flex flex-wrap gap-4" data-testid="lemonade-order-info">
            <div className="rounded-xl border-2 border-[#1a1a1a] bg-yellow-300 shadow-[4px_4px_0_#1a1a1a] px-5 py-3.5">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em]">🕐 Open Hours</p>
              <p className="text-sm font-bold mt-1">Sat &amp; Sun · 8am – 8pm</p>
              <p className="text-xs text-zinc-600">Weekends only</p>
            </div>
            <div className="rounded-xl border-2 border-[#1a1a1a] bg-pink-400 shadow-[4px_4px_0_#1a1a1a] px-5 py-3.5 text-white">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em]">🎉 Bulk Orders</p>
              <a href={CONTACT.phoneHref} data-testid="lemonade-text-reserve-link" className="block text-sm font-bold mt-1 underline underline-offset-2">
                Text {CONTACT.phone}
              </a>
              <p className="text-xs text-pink-100">Any day · 6+ drinks · one drop-off · FREE delivery</p>
            </div>
          </div>
        </motion.div>
        <motion.div {...fade(0.25)} className="hidden lg:block">
          <img src={brand.flyer} alt="Lani Lemonade flyer" className="rounded-2xl w-full max-w-lg ml-auto shadow-2xl" />
        </motion.div>
      </div>
    </div>
  </section>
);

const ShopPage = ({ slug }) => {
  const brand = BRANDS[slug];
  const { products: all } = useProducts();
  if (!brand) return <Navigate to="/" replace />;
  const products = all.filter((p) => p.brand === slug);
  const light = brand.theme === "light";

  return (
    <main data-testid={`shop-${slug}-page`} className={light ? "bg-gradient-to-b from-amber-50 to-pink-50 text-[#1a1a1a]" : ""}>
      {light ? <LemonadeHero brand={brand} /> : <DarkHero brand={brand} isBling={slug === "bling"} />}

      <section id="collection" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        {light ? (
          LEMONADE_SECTIONS.map((sec) => {
            const items = products.filter((p) => p.cat === sec.cat);
            if (!items.length) return null;
            return (
              <div key={sec.cat} className="mb-14" data-testid={`section-${sec.cat.toLowerCase().replace(" ", "-")}`}>
                <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#1a1a1a]">{sec.title}</h2>
                    <p className="text-sm text-zinc-500 mt-1">{sec.sub}</p>
                  </div>
                  <span className="rounded-full border-2 border-[#1a1a1a] px-3.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-pink-500">
                    {items.length} option{items.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid={`shop-${slug}-grid-${sec.cat.toLowerCase().replace(" ", "-")}`}>
                  {items.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300/80">Collection</p>
            <h2 className="mt-3 font-serif2 font-semibold text-4xl sm:text-5xl text-white" data-testid={`shop-${slug}-collection-title`}>
              Shop All {products.length}
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid={`shop-${slug}-grid`}>
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </>
        )}
      </section>

      {light && <BulkOrderForm />}
    </main>
  );
};

export default ShopPage;
